/**
 * Equipment Repository
 * 
 * Consolidates equipment registry systems:
 * - equipment
 * - sw_equipment
 * - equipment_registry
 * 
 * Into unified equipment_unified table
 * 
 * @example
 * ```typescript
 * const repo = new EquipmentRepository();
 * 
 * // Find all equipment
 * const equipment = await repo.findAll();
 * 
 * // Find by customer
 * const customerEquip = await repo.findByCustomer('customer-123');
 * 
 * // Find by warranty status
 * const underWarranty = await repo.findUnderWarranty();
 * 
 * // Get statistics
 * const stats = await repo.getStatistics();
 * ```
 */

import { BaseRepository, QueryOptions } from './BaseRepository';

/**
 * Equipment Type
 */
export interface Equipment {
  id: string;
  company_id: string;
  customer_id: string;
  
  // Equipment identification
  equipment_type: 'hvac' | 'heating' | 'cooling' | 'water_heater' | 'plumbing' | 'electrical' | 'other';
  make: string;
  model: string;
  serial_number?: string;
  year_manufactured?: number;
  
  // Location details
  location_type: 'primary' | 'secondary' | 'rental' | 'commercial';
  location_description?: string;
  address_id?: string;
  
  // Installation details
  installation_date?: string;
  installed_by?: string;
  installer_company?: string;
  
  // Warranty information
  warranty_start_date?: string;
  warranty_end_date?: string;
  warranty_type?: 'manufacturer' | 'extended' | 'service_agreement' | 'none';
  warranty_provider?: string;
  warranty_coverage?: string;
  is_under_warranty: boolean;
  
  // Service history
  last_service_date?: string;
  last_service_type?: string;
  last_service_technician?: string;
  next_service_due?: string;
  service_frequency?: 'monthly' | 'quarterly' | 'semi-annual' | 'annual' | 'as-needed';
  
  // Equipment condition
  condition: 'new' | 'good' | 'fair' | 'poor' | 'critical' | 'unknown';
  efficiency_rating?: string;
  estimated_lifespan_years?: number;
  age_years?: number;
  
  // Specifications
  capacity?: string;
  fuel_type?: 'electric' | 'gas' | 'oil' | 'propane' | 'solar' | 'other';
  voltage?: string;
  amperage?: string;
  btu_rating?: number;
  seer_rating?: number;
  
  // Status tracking
  status: 'active' | 'inactive' | 'replaced' | 'removed' | 'repair_needed';
  replacement_date?: string;
  replacement_equipment_id?: string;
  replacement_reason?: string;
  
  // Maintenance tracking
  maintenance_plan_id?: string;
  requires_seasonal_maintenance: boolean;
  seasonal_prep_spring?: boolean;
  seasonal_prep_fall?: boolean;
  
  // Notes and documentation
  notes?: string;
  installation_notes?: string;
  service_notes?: string;
  customer_notes?: string;
  
  // Attachments
  photos?: string[];
  documents?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
  }>;
  
  // Related equipment
  system_id?: string;
  related_equipment_ids?: string[];
  
  // Source tracking (for migration)
  source_system?: 'equipment' | 'sw_equipment' | 'equipment_registry';
  source_id?: string;
  sw_equipment_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  deactivated_at?: string;
}

/**
 * Equipment Filters
 */
export interface EquipmentFilters {
  company_id?: string;
  customer_id?: string;
  equipment_type?: Equipment['equipment_type'] | Equipment['equipment_type'][];
  make?: string;
  model?: string;
  serial_number?: string;
  location_type?: Equipment['location_type'];
  warranty_type?: Equipment['warranty_type'];
  is_under_warranty?: boolean;
  condition?: Equipment['condition'] | Equipment['condition'][];
  status?: Equipment['status'] | Equipment['status'][];
  fuel_type?: Equipment['fuel_type'];
  requires_seasonal_maintenance?: boolean;
  needs_service?: boolean;
  warranty_expiring_days?: number;
  installed_after?: string;
  installed_before?: string;
  search_query?: string;
}

/**
 * Equipment Statistics
 */
export interface EquipmentStatistics {
  total_equipment: number;
  active_equipment: number;
  inactive_equipment: number;
  under_warranty: number;
  warranty_expiring_soon: number;
  needs_service: number;
  by_type: Record<string, number>;
  by_condition: Record<string, number>;
  by_status: Record<string, number>;
  by_warranty_type: Record<string, number>;
  average_age_years: number;
  oldest_equipment: Array<{
    id: string;
    make: string;
    model: string;
    age_years: number;
  }>;
  warranty_expiring: Array<{
    id: string;
    customer_id: string;
    make: string;
    model: string;
    warranty_end_date: string;
  }>;
}

/**
 * Equipment Repository Class
 * 
 * Provides data access methods for equipment with
 * advanced filtering, warranty tracking, and maintenance scheduling.
 */
export class EquipmentRepository extends BaseRepository<Equipment> {
  constructor() {
    super({
      tableName: 'equipment_unified',
      primaryKey: 'id'
    });
  }

  /**
   * Find equipment by filters
   */
  async findByFilters(
    filters: EquipmentFilters,
    options?: QueryOptions
  ): Promise<Equipment[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Apply filters
    if (filters.company_id) {
      query = query.eq('company_id', filters.company_id);
    }

    if (filters.customer_id) {
      query = query.eq('customer_id', filters.customer_id);
    }

    if (filters.equipment_type) {
      if (Array.isArray(filters.equipment_type)) {
        query = query.in('equipment_type', filters.equipment_type);
      } else {
        query = query.eq('equipment_type', filters.equipment_type);
      }
    }

    if (filters.make) {
      query = query.ilike('make', `%${filters.make}%`);
    }

    if (filters.model) {
      query = query.ilike('model', `%${filters.model}%`);
    }

    if (filters.serial_number) {
      query = query.eq('serial_number', filters.serial_number);
    }

    if (filters.location_type) {
      query = query.eq('location_type', filters.location_type);
    }

    if (filters.warranty_type) {
      query = query.eq('warranty_type', filters.warranty_type);
    }

    if (filters.is_under_warranty !== undefined) {
      query = query.eq('is_under_warranty', filters.is_under_warranty);
    }

    if (filters.condition) {
      if (Array.isArray(filters.condition)) {
        query = query.in('condition', filters.condition);
      } else {
        query = query.eq('condition', filters.condition);
      }
    }

    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.fuel_type) {
      query = query.eq('fuel_type', filters.fuel_type);
    }

    if (filters.requires_seasonal_maintenance !== undefined) {
      query = query.eq('requires_seasonal_maintenance', filters.requires_seasonal_maintenance);
    }

    if (filters.needs_service) {
      const today = new Date().toISOString();
      query = query.lte('next_service_due', today);
    }

    if (filters.warranty_expiring_days) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + filters.warranty_expiring_days);
      query = query
        .lte('warranty_end_date', futureDate.toISOString())
        .gte('warranty_end_date', new Date().toISOString());
    }

    if (filters.installed_after) {
      query = query.gte('installation_date', filters.installed_after);
    }

    if (filters.installed_before) {
      query = query.lte('installation_date', filters.installed_before);
    }

    if (filters.search_query) {
      query = query.or(`
        make.ilike.%${filters.search_query}%,
        model.ilike.%${filters.search_query}%,
        serial_number.ilike.%${filters.search_query}%,
        notes.ilike.%${filters.search_query}%
      `);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default ordering by updated_at
      query = query.order('updated_at', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  /**
   * Find equipment by customer
   */
  async findByCustomer(
    customerId: string,
    options?: QueryOptions
  ): Promise<Equipment[]> {
    return this.findByFilters({ customer_id: customerId, status: 'active' }, options);
  }

  /**
   * Find equipment under warranty
   */
  async findUnderWarranty(
    options?: QueryOptions
  ): Promise<Equipment[]> {
    return this.findByFilters({ is_under_warranty: true, status: 'active' }, options);
  }

  /**
   * Find equipment with warranty expiring soon
   */
  async findWarrantyExpiringSoon(
    days: number = 90,
    options?: QueryOptions
  ): Promise<Equipment[]> {
    return this.findByFilters({ warranty_expiring_days: days, status: 'active' }, options);
  }

  /**
   * Find equipment needing service
   */
  async findNeedingService(
    options?: QueryOptions
  ): Promise<Equipment[]> {
    return this.findByFilters({ needs_service: true, status: 'active' }, options);
  }

  /**
   * Find equipment by serial number
   */
  async findBySerialNumber(
    serialNumber: string
  ): Promise<Equipment | null> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('serial_number', serialNumber)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as Equipment;
  }

  /**
   * Find equipment by type
   */
  async findByType(
    type: Equipment['equipment_type'],
    options?: QueryOptions
  ): Promise<Equipment[]> {
    return this.findByFilters({ equipment_type: type, status: 'active' }, options);
  }

  /**
   * Find equipment requiring seasonal maintenance
   */
  async findRequiringSeasonalMaintenance(
    season: 'spring' | 'fall',
    options?: QueryOptions
  ): Promise<Equipment[]> {
    const column = season === 'spring' ? 'seasonal_prep_spring' : 'seasonal_prep_fall';
    
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('status', 'active')
      .eq('requires_seasonal_maintenance', true)
      .eq(column, false);

    if (error) throw error;
    return data || [];
  }

  /**
   * Mark seasonal maintenance complete
   */
  async markSeasonalMaintenanceComplete(
    equipmentId: string,
    season: 'spring' | 'fall'
  ): Promise<Equipment> {
    const column = season === 'spring' ? 'seasonal_prep_spring' : 'seasonal_prep_fall';
    return this.update(equipmentId, {
      [column]: true,
      last_service_date: new Date().toISOString()
    } as any);
  }

  /**
   * Get equipment statistics
   */
  async getStatistics(): Promise<EquipmentStatistics> {
    const { data: equipment, error } = await this.client
      .from(this.config.tableName)
      .select('*');

    if (error) throw error;

    const stats: EquipmentStatistics = {
      total_equipment: equipment?.length || 0,
      active_equipment: 0,
      inactive_equipment: 0,
      under_warranty: 0,
      warranty_expiring_soon: 0,
      needs_service: 0,
      by_type: {},
      by_condition: {},
      by_status: {},
      by_warranty_type: {},
      average_age_years: 0,
      oldest_equipment: [],
      warranty_expiring: []
    };

    if (!equipment) return stats;

    const today = new Date();
    const in90Days = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);

    let totalAge = 0;
    let equipmentWithAge = 0;

    equipment.forEach(equip => {
      // Status counts
      if (equip.status === 'active') stats.active_equipment++;
      else stats.inactive_equipment++;

      // Warranty counts
      if (equip.is_under_warranty) stats.under_warranty++;

      // Warranty expiring soon
      if (equip.warranty_end_date) {
        const warrantyEnd = new Date(equip.warranty_end_date);
        if (warrantyEnd > today && warrantyEnd < in90Days) {
          stats.warranty_expiring_soon++;
          stats.warranty_expiring.push({
            id: equip.id,
            customer_id: equip.customer_id,
            make: equip.make,
            model: equip.model,
            warranty_end_date: equip.warranty_end_date
          });
        }
      }

      // Service needed
      if (equip.next_service_due && new Date(equip.next_service_due) <= today) {
        stats.needs_service++;
      }

      // Type breakdown
      stats.by_type[equip.equipment_type] = (stats.by_type[equip.equipment_type] || 0) + 1;

      // Condition breakdown
      stats.by_condition[equip.condition] = (stats.by_condition[equip.condition] || 0) + 1;

      // Status breakdown
      stats.by_status[equip.status] = (stats.by_status[equip.status] || 0) + 1;

      // Warranty type breakdown
      if (equip.warranty_type) {
        stats.by_warranty_type[equip.warranty_type] = (stats.by_warranty_type[equip.warranty_type] || 0) + 1;
      }

      // Age calculation
      if (equip.age_years) {
        totalAge += equip.age_years;
        equipmentWithAge++;
      }
    });

    // Calculate average age
    stats.average_age_years = equipmentWithAge > 0 ? totalAge / equipmentWithAge : 0;

    // Find oldest equipment (top 10)
    stats.oldest_equipment = equipment
      .filter(e => e.age_years)
      .sort((a, b) => (b.age_years || 0) - (a.age_years || 0))
      .slice(0, 10)
      .map(equip => ({
        id: equip.id,
        make: equip.make,
        model: equip.model,
        age_years: equip.age_years || 0
      }));

    // Sort warranty expiring by date
    stats.warranty_expiring.sort((a, b) => 
      new Date(a.warranty_end_date).getTime() - new Date(b.warranty_end_date).getTime()
    );

    return stats;
  }

  /**
   * Replace equipment
   */
  async replaceEquipment(
    oldEquipmentId: string,
    newEquipmentData: Partial<Equipment>,
    replacementReason: string
  ): Promise<{ old: Equipment; new: Equipment }> {
    // Mark old equipment as replaced
    const oldEquipment = await this.update(oldEquipmentId, {
      status: 'replaced',
      replacement_date: new Date().toISOString(),
      replacement_reason: replacementReason
    });

    // Create new equipment
    const newEquipment = await this.create({
      ...newEquipmentData,
      status: 'active',
      replacement_equipment_id: oldEquipmentId
    });

    // Update old equipment with new equipment ID
    await this.update(oldEquipmentId, {
      replacement_equipment_id: newEquipment.id
    });

    return { old: oldEquipment, new: newEquipment };
  }

  /**
   * Get equipment type breakdown
   */
  async getTypeBreakdown(): Promise<Record<string, number>> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('equipment_type')
      .eq('status', 'active');

    if (error) throw error;

    const breakdown: Record<string, number> = {};
    data?.forEach(equip => {
      breakdown[equip.equipment_type] = (breakdown[equip.equipment_type] || 0) + 1;
    });

    return breakdown;
  }

  /**
   * Schedule next service
   */
  async scheduleNextService(
    equipmentId: string,
    nextServiceDate: string,
    serviceType?: string
  ): Promise<Equipment> {
    return this.update(equipmentId, {
      next_service_due: nextServiceDate,
      last_service_type: serviceType
    });
  }
}
